/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./forgotpassword.scss";
import { BrandButton } from "../components/form/buttons";
import { BrandInput } from "../components/form/inputs";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Snackbar, Alert } from "@mui/material";
import { ApiEndpoint } from "../components/utils";
import BackButton from "../components/backBtn";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [queryParameters] = useSearchParams();
  const resetKey = useRef("");
  const [snackError, setSnackError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const vertical = "bottom";
  const horizontal = "center";
  const validEmail = z.object({
    email: z
      .string()
      .min(1, { message: "Email field cannot be empty." })
      .email("This is not a valid email."),
  });
  const notEmptyString = z
    .string()
    .regex(/^(?=.*\d)(?=.*[a-zA-Z])(?=.*[!#$%&?]).{8,}$/);

  const username = useRef("");
  const password = useRef("");

  const validatePasswordReset = async () => {
    const checkPassword = notEmptyString.safeParse(password.current);

    if (!checkPassword.success) {
      setErrorMsg(
        "Please supply a password that fits the requirements as outlined on this page."
      );
      setSnackError(true);
    } else {
      try {
        const resp = await axios.post(
          ApiEndpoint + "wp-json/satapp-api/v1/resetPassword",
          {
            reset_key: resetKey.current,
            password: password.current,
          }
        );
        if (resp.data.success) {
          setPasswordUpdated(true);
          setShowUpdateForm(false);
        } else {
          setErrorMsg(resp.data.message);
          setSnackError(true);
        }
      } catch (e) {
        setErrorMsg(
          "There was a problem resetting your password. Please try again."
        );
        setSnackError(true);
      }
    }
  };

  const validateForm = async () => {
    const checkEmail = validEmail.safeParse({ email: username.current });
    if (!checkEmail.success) {
      setErrorMsg(
        "Email or password field are in incorrect format. Please try again."
      );
      setSnackError(true);
    } else {
      try {
        const resp = await axios.post(
          ApiEndpoint + "wp-json/satapp-api/v1/forgotPassword",
          {
            email: username.current,
          }
        );
        if (resp.data.success) {
          setLinkSent(true);
        } else {
          setErrorMsg(resp.data.message);
          setSnackError(true);
        }
      } catch (e) {
        setErrorMsg(
          "There was a problem sending your password reset link. Please try again."
        );
        setSnackError(true);
      }
    }
  };

  const closeSnack = () => {
    setSnackError(false);
  };

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (!linkSent && !showUpdateForm && !passwordUpdated) {
          validateForm();
        } 
        if (showUpdateForm) {
          validatePasswordReset();
        }
      }
    }
    document.addEventListener('keydown', onEnter);
    return () => {
      document.removeEventListener('keydown', onEnter);
    };
  }, [linkSent, passwordUpdated, showUpdateForm, validateForm, validatePasswordReset]);

  useEffect(() => {
    const key = queryParameters.get("reset_key");
    if (key) {
      resetKey.current = key;
      setShowUpdateForm(true);
    }
  }, [queryParameters]);

  return (
    <div className="forgotPasswordPage">
      <BackButton navigate={navigate} destination="/login" text="back" />
      <div className="headline">
        <h1>Password Reset</h1>
        <div className="description">
          {showUpdateForm
            ? `Please enter your new password below.`
            : !passwordUpdated
            ? `Please enter your email address and we will send you a link to reset your password.`
            : ``}
        </div>
      </div>

      <div className="loginForm">
        {!linkSent && !showUpdateForm && !passwordUpdated && (
          <div className="formRow">
            <BrandInput
              onChange={(evt) => (username.current = evt.currentTarget.value)}
              id="filled-basic"
              label="Email"
              variant="filled"
            />
            <div className="btnHolder">
              <BrandButton onClick={validateForm}>SEND RESET LINK</BrandButton>
            </div>
          </div>
        )}

        {linkSent && !showUpdateForm && !passwordUpdated && (
          <div className="formRow">
            <h3>
              A reset link has been sent.{" "}
              <Link to="/login">Go to Login Page</Link>
            </h3>
          </div>
        )}

        {showUpdateForm && (
          <>
            <div className="formRow">
              <BrandInput
                onChange={(evt) => (password.current = evt.currentTarget.value)}
                id="filled-bas)ic"
                label="New Password"
                type="password"
                variant="filled"
              />
              <div className="btnHolder">
                <BrandButton onClick={validatePasswordReset}>
                  UPDATE PASSWORD
                </BrandButton>
              </div>
            </div>
            <div>
              <p>
                <strong>Password Rules:</strong>
              </p>
              <ul>
                <li>At least 8 characters long</li>
                <li>Contain at least one UPPERCASE letter</li>
                <li>Contain at least one lowercase letter</li>
                <li>Contain at least one number</li>
                <li>Contain at least one of these special characters !#$%&?</li>
              </ul>
            </div>
          </>
        )}

        {!showUpdateForm && passwordUpdated && (
          <div className="formRow">
            <h3>
              Your password has been updated!{" "}
              <Link to="/login">Go to Login Page</Link>
            </h3>
          </div>
        )}
      </div>
      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
    </div>
  );
};

export default ForgotPassword;
